import React, { useEffect, useMemo, useState } from "react";
import "./community.css";
import {
  Box, Stack, Typography, IconButton, Avatar, Button, Dialog,
  DialogContent, DialogTitle, DialogActions, TextField, Tabs, Tab,
  Menu, MenuItem, CircularProgress, Divider, Chip
} from "@mui/material";
import {
  ArrowBack, MoreVert, MoreHoriz, Edit, Delete, ShareOutlined,
  ThumbUpOffAlt, ChatBubbleOutline, Send, Image as ImageIcon, VideoLibrary, Close
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import backend from "../../../../../api";

/* constants */
const BRAND = "#FCAA30";
const VIDEO_EXTS = ["mp4", "mov", "mkv", "webm", "avi", "m4v", "3gp"];
const inferType = (url, mime = "") => {
  const m = String(mime).toLowerCase();
  if (m.startsWith("video/")) return "video";
  const ext = (url || "").split("?")[0].split(".").pop().toLowerCase();
  return VIDEO_EXTS.includes(ext) ? "video" : "image";
};
const UPLOAD_URL = "https://aws-calendia-bc10efcc84bf.herokuapp.com/api/deck/upload";

/* generic confirm dialog */
function Confirm({ open, onClose, onConfirm, title, message, cta = "Confirm", danger }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      {!!message && <DialogContent>{message}</DialogContent>}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color={danger ? "error" : "primary"} variant="contained">{cta}</Button>
      </DialogActions>
    </Dialog>
  );
}

/* media block (images + video) */
function MediaBlock({ media = [], expanded, onSeeMore }) {
  const n = media.length;
  if (!n) return null;

  const render = (m, cls) => m.type === "video"
    ? <video src={m.url} className={cls} controls muted loop />
    : <img src={m.url} alt="" className={cls} loading="lazy" />;

  if (n === 1 || (expanded && n === 1)) {
    return <Box sx={{ mt: 1.5 }}>{render(media[0], "comm-media-single")}</Box>;
  }
  if (n === 2 || (expanded && n === 2)) {
    return (
      <div className="comm-row2">
        {render(media[0], "comm-duo")}
        {render(media[1], "comm-duo")}
      </div>
    );
  }
  const first3 = media.slice(0, 3);
  return (
    <Box>
      <div className="comm-grid">
        {render(first3[0], "comm-g1")}
        <div className="comm-gcol">
          {render(first3[1] || first3[0], "comm-g2")}
          {render(first3[2] || first3[1] || first3[0], "comm-g2")}
        </div>
      </div>
      {!expanded && media.length > 3 && (
        <span className="comm-seemore" onClick={onSeeMore}>
          See more ({media.length - 3} more)
        </span>
      )}
      {expanded && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {media.slice(3).reduce((rows, item, i) => {
            if (i % 2 === 0) rows.push([item]); else rows[rows.length - 1].push(item);
            return rows;
          }, []).map((row, idx) => (
            <div className="comm-row2" key={idx}>
              {render(row[0], "comm-duo")}
              {row[1] ? render(row[1], "comm-duo") : <div style={{ flex: 1 }} />}
            </div>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/* compose dialog */
function ComposeDialog({ open, onClose, onPublish }) {
  const { user } = useSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [imgBusy, setImgBusy] = useState(false);
  const [vidBusy, setVidBusy] = useState(false);

  const isValid = text.trim() || media.length > 0;

  const gridPreview = useMemo(() => {
    const n = media.length;
    if (!n) return null;
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: "var(--subtle)" }}>
          Media ({n})
        </Typography>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
            gap: 10
          }}
        >
          {media.map((m, i) => (
            <div key={i} style={{ position: "relative", borderRadius: 8, overflow: "hidden" }}>
              {m.type === "video" ? (
                <video
                  src={m.url}
                  style={{ width: "100%", height: 110, objectFit: "cover" }}
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img
                  src={m.url}
                  alt=""
                  style={{ width: "100%", height: 110, objectFit: "cover" }}
                  loading="lazy"
                />
              )}
              <IconButton
                onClick={() => setMedia(prev => prev.filter((_, idx) => idx !== i))}
                size="small"
                aria-label="Remove media"
                title="Remove"
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  background: "rgba(17,24,39,0.6)",
                  color: "#fff",
                  borderRadius: 8
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      </Box>
    );
  }, [media]);

  const uploadFiles = async (files, setBusy) => {
    try {
      setBusy(true);
      const fd = new FormData();
      [...files].forEach(f => fd.append("files", f));
      const r = await backend.post(UPLOAD_URL, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = (r.data?.data || []).map(d => ({ url: d.imgurl, type: inferType(d.imgurl, d.mimetype) }));
      setMedia(prev => [...prev, ...items]);
      enqueueSnackbar("Media uploaded successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to upload media", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handlePick = (accept, kind) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = accept;
    input.onchange = (e) => {
      if (!e.target.files?.length) return;
      uploadFiles(
        e.target.files,
        kind === "image" ? setImgBusy : setVidBusy
      );
    };
    input.click();
  };

  const publish = async () => {
    if (!isValid) {
      enqueueSnackbar("Please add a description or media", { variant: "error" });
      return;
    }
    if (imgBusy || vidBusy) {
      enqueueSnackbar("Please wait for media to finish uploading", { variant: "error" });
      return;
    }
    try {
      await onPublish({ text, media });
      enqueueSnackbar("Post created successfully", { variant: "success" });
      setText(""); setMedia([]);
      onClose();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to create post", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Create new post
        <IconButton onClick={onClose} sx={{ float: "right" }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar src={user?.imageURL} />
          <Box>
            <Typography fontWeight={800}>{user?.name || "Member"}</Typography>
            <Typography variant="caption" sx={{ color: "var(--subtle)" }}>9:30 am</Typography>
          </Box>
        </Stack>

        <TextField
          multiline rows={4}
          placeholder="Describe about your post ..."
          sx={{ mt: 2 }}
          value={text}
          onChange={e => setText(e.target.value)}
          error={!text.trim() && media.length === 0}
          helperText={!text.trim() && media.length === 0 ? "Description or media is required" : ""}
        />

        {media.length > 0 && gridPreview}

        <Stack direction="row" spacing={1.2} sx={{ mt: 1.5 }}>
          <Button
            onClick={() => handlePick("image/*", "image")}
            className="comm-pick"
            fullWidth
            startIcon={<ImageIcon />}
            disabled={imgBusy}
          >
            {imgBusy ? <CircularProgress size={18} /> : "Upload photo"}
          </Button>
          <Button
            onClick={() => handlePick("video/*", "video")}
            className="comm-pick"
            fullWidth
            startIcon={<VideoLibrary />}
            disabled={vidBusy}
          >
            {vidBusy ? <CircularProgress size={18} /> : "Upload video"}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={imgBusy || vidBusy}>Cancel</Button>
        <Button
          onClick={publish}
          disabled={!isValid || imgBusy || vidBusy}
          variant="contained"
          sx={{ background: BRAND, color: "#111827", fontWeight: 800 }}
        >
          {(imgBusy || vidBusy) ? <CircularProgress size={18} /> : "Post"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* comments dialog */
function CommentsDialog({ post, onClose, onSend, onStartEdit, onDelete }) {
  const { user } = useSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;
  const [text, setText] = useState("");
  const [menuEl, setMenuEl] = useState(null);
  const [selIndex, setSelIndex] = useState(null);

  if (!post) return null;

  const handleSend = async () => {
    if (!text.trim()) {
      enqueueSnackbar("Please enter a comment", { variant: "error" });
      return;
    }
    try {
      await onSend(text);
      enqueueSnackbar("Comment added successfully", { variant: "success" });
      setText("");
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to add comment", { variant: "error" });
    }
  };

  return (
    <Dialog open={Boolean(post)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        All comments
        <IconButton onClick={onClose} sx={{ float: "right" }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        {post.comments?.length ? post.comments.map((c, idx) => (
          <Stack key={idx} direction="row" spacing={1.2} sx={{ mb: 1.25 }}>
            <Avatar src={c.author?.imageURL} />
            <Box flex={1}>
              <Stack direction="row" alignItems="center">
                <Typography fontWeight={800}>{c.author?.name || "Member"}</Typography>
                {String(c.author?._id) === String(userId) && (
                  <IconButton size="small" onClick={(e) => { setMenuEl(e.currentTarget); setSelIndex(idx); }}>
                    <MoreHoriz fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <Typography sx={{ mt: .25 }}>{c.text}</Typography>
            </Box>
          </Stack>
        )) : <Typography sx={{ color: "var(--subtle)" }}>No comments yet.</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 2 }}>
        <TextField
          placeholder="Write your message"
          value={text}
          onChange={e => setText(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          error={!text.trim()}
          helperText={!text.trim() ? "Comment is required" : ""}
        />
        <IconButton color="primary" onClick={handleSend}>
          <Send />
        </IconButton>
      </DialogActions>

      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        <MenuItem onClick={() => { onStartEdit(selIndex, post.comments[selIndex]?.text || ""); setMenuEl(null); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => { onDelete(selIndex); setMenuEl(null); }} sx={{ color: "error.main" }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Dialog>
  );
}

/* Post card */
function PostCard({ post, expanded, onSeeMore, onLike, onComment, onMore }) {
  return (
    <Box className="comm-post">
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar src={post.author?.imageURL} />
        <Box flex={1} minWidth={0}>
          <Typography className="author" noWrap>{post.author?.name || "Member"}</Typography>
          <Typography className="time">{new Date(post.createdAt).toDateString()}</Typography>
        </Box>
        {post._canEdit && (
          <IconButton onClick={(e) => onMore(e.currentTarget)}><MoreHoriz /></IconButton>
        )}
      </Stack>

      {!!post.text && <Typography className="body">{post.text}</Typography>}

      <MediaBlock media={post.media} expanded={expanded} onSeeMore={onSeeMore} />

      <div className="comm-actions">
        <span onClick={onLike} style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
          <ThumbUpOffAlt sx={{ color: "var(--subtle)" }} />
          <span className="count">{post.likesCount || 0}</span>
        </span>
        <span onClick={onComment} style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
          <ChatBubbleOutline sx={{ color: "var(--subtle)" }} />
          <span className="count">{post.commentsCount || 0}</span>
        </span>
        <span
          onClick={() => navigator.share?.({ text: post.text || "Check out this community post" }).catch(() => { })}
          style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
        >
          <ShareOutlined sx={{ color: "var(--subtle)" }} />
          <span className="count">Share</span>
        </span>
      </div>
    </Box>
  );
}

/* Admin: Manage Members dialog */
function ManageMembersDialog({ communityId, open, onClose, isAdmin, currentUserId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);

  const load = async () => {
    if (!open || !isAdmin) return;
    setLoading(true);
    try {
      const r = await backend.get(`/api/community/${communityId}/members`, { params: { userId: currentUserId } });
      setMembers(r.data?.data || []);
      // enqueueSnackbar("Members loaded successfully", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || "Failed to load members", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [open]);

  const removeMember = async (uid) => {
    try {
      await backend.delete(`/api/community/${communityId}/member/${uid}`, { data: { userId: currentUserId } });
      enqueueSnackbar("Member removed successfully", { variant: "success" });
      setMembers(prev => prev.filter(m => m._id !== uid));
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || "Failed to remove member", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Manage members
        <IconButton onClick={onClose} sx={{ float: "right" }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1.2}>
            {members.length === 0 && (
              <Typography sx={{ color: "var(--subtle)" }}>No members.</Typography>
            )}
            {members.map(m => (
              <Stack key={m._id} direction="row" alignItems="center" spacing={1}>
                <Avatar src={m.imageURL} />
                <Box flex={1} minWidth={0}>
                  <Typography noWrap>{m.name || "Member"}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: .25 }}>
                    {m.isOwner && <Chip size="small" label="Owner" color="warning" />}
                    {m.isAdmin && !m.isOwner && <Chip size="small" label="Admin" />}
                  </Stack>
                </Box>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeMember(m._id)}
                  disabled={m.isOwner}
                >
                  Remove
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function CommunityDetails() {
  const nav = useNavigate();
  const { id: cid } = useParams();
  const { user } = useSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;

  const [loading, setLoading] = useState(true);
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("all");
  const [posts, setPosts] = useState([]);

  const [expanded, setExpanded] = useState(() => new Set());

  const [postMenuEl, setPostMenuEl] = useState(null);
  const [postForMenu, setPostForMenu] = useState(null);

  const [headerMenuEl, setHeaderMenuEl] = useState(null);

  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDeletePost, setConfirmDeletePost] = useState(null);
  const [confirmDeleteComment, setConfirmDeleteComment] = useState(null);
  const [confirmDeleteCommunity, setConfirmDeleteCommunity] = useState(false);

  const [openCompose, setOpenCompose] = useState(false);
  const [openCommentsPost, setOpenCommentsPost] = useState(null);

  const [editPost, setEditPost] = useState(null);
  const [editPostText, setEditPostText] = useState("");
  const [editPostMedia, setEditPostMedia] = useState([]);
  const [editImgBusy, setEditImgBusy] = useState(false);
  const [editVidBusy, setEditVidBusy] = useState(false);

  const [editComment, setEditComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");

  const [openManage, setOpenManage] = useState(false);

  const isAdmin = !!c?._isAdmin;

  const isEditPostValid = editPostText.trim() || editPostMedia.length > 0;
  const isEditCommentValid = editCommentText.trim();

  const loadPosts = async (communityId, mode) => {
    if (!communityId || !userId) {
      if (!userId) {
        enqueueSnackbar("Please log in to view posts", { variant: "error" });
        nav("/login");
      }
      return;
    }
    try {
      const params = { userId, author: mode === "mine" ? "me" : undefined };
      const r = await backend.get(`/api/community/${communityId}/posts`, { params });
      setPosts(r.data?.data || []);
      // enqueueSnackbar("Posts loaded successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to load posts", { variant: "error" });
    }
  };

  const load = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const params = userId ? { userId } : {};
      const r = await backend.get(`/api/community/${cid}`, { params });
      setC(r.data?.data);
      await loadPosts(r.data?.data?._id, tab);
      // enqueueSnackbar("Community loaded successfully", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || "Failed to load community", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [cid, userId]); // eslint-disable-line
  useEffect(() => { if (c?._id) loadPosts(c._id, tab); }, [tab, c?._id]); // eslint-disable-line

  useEffect(() => {
    if (editPost) {
      setEditPostText(editPost.text || "");
      setEditPostMedia(editPost.media || []);
    }
  }, [editPost]);

  useEffect(() => { if (editComment) setEditCommentText(editComment.currentText || ""); }, [editComment]);

  const toggleJoin = async () => {
    if (!userId) {
      enqueueSnackbar("Please log in to join/leave a community", { variant: "error" });
      nav("/login");
      return;
    }
    if (c?._joined) {
      setConfirmLeave(true);
      return;
    }
    try {
      await backend.put(`/api/community/${cid}/join-toggle`, { userId });
      enqueueSnackbar("Successfully joined community", { variant: "success" });
      load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to join community", { variant: "error" });
    }
  };

  const actuallyLeave = async () => {
    try {
      await backend.put(`/api/community/${cid}/join-toggle`, { userId });
      enqueueSnackbar("Successfully left community", { variant: "success" });
      setConfirmLeave(false);
      load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to leave community", { variant: "error" });
    }
  };

  const onPublish = async ({ text, media }) => {
    if (!userId) {
      enqueueSnackbar("Please log in to create a post", { variant: "error" });
      nav("/login");
      return;
    }
    try {
      await backend.post(`/api/community/${cid}/posts`, { userId, text, media });
      enqueueSnackbar("Post created successfully", { variant: "success" });
      setOpenCompose(false);
      loadPosts(cid, tab);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to create post", { variant: "error" });
    }
  };

  const onLike = async (post) => {
    if (!userId) {
      enqueueSnackbar("Please log in to like a post", { variant: "error" });
      nav("/login");
      return;
    }
    try {
      await backend.post(`/api/community/posts/${post._id}/like-toggle`, { userId });
      enqueueSnackbar("Liked", { variant: "success" });
      loadPosts(cid, tab);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to like/unlike post", { variant: "error" });
    }
  };

  const deletePost = async (post) => {
    try {
      await backend.delete(`/api/community/posts/${post._id}`, { data: { userId } });
      enqueueSnackbar("Post deleted successfully", { variant: "success" });
      setConfirmDeletePost(null);
      loadPosts(cid, tab);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to delete post", { variant: "error" });
    }
  };

  const deleteComment = async ({ postId, index }) => {
    try {
      await backend.delete(`/api/community/posts/${postId}/comment/${index}`, { data: { userId } });
      enqueueSnackbar("Comment deleted successfully", { variant: "success" });
      setConfirmDeleteComment(null);
      loadPosts(cid, tab);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to delete comment", { variant: "error" });
    }
  };

  const removeEditMediaAt = (index) => {
    setEditPostMedia(prev => prev.filter((_, i) => i !== index));
  };

  const uploadEditFiles = async (files, setBusy) => {
    try {
      setBusy(true);
      const fd = new FormData();
      [...files].forEach(f => fd.append("files", f));
      const r = await backend.post(UPLOAD_URL, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = (r.data?.data || []).map(d => ({ url: d.imgurl, type: inferType(d.imgurl, d.mimetype) }));
      setEditPostMedia(prev => [...prev, ...items]);
      enqueueSnackbar("Media uploaded successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to upload media", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleEditPick = (accept, kind) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = accept;
    input.onchange = (e) => {
      if (!e.target.files?.length) return;
      uploadEditFiles(e.target.files, kind === "image" ? setEditImgBusy : setEditVidBusy);
    };
    input.click();
  };

  const saveEditPost = async () => {
    if (!isEditPostValid) {
      enqueueSnackbar("Please add a description or media", { variant: "error" });
      return;
    }
    if (editImgBusy || editVidBusy) {
      enqueueSnackbar("Please wait for media to finish uploading", { variant: "error" });
      return;
    }
    try {
      await backend.put(`/api/community/posts/${editPost._id}`, {
        userId,
        text: editPostText,
        media: editPostMedia,
      });
      enqueueSnackbar("Post updated successfully", { variant: "success" });
      setEditPost(null);
      loadPosts(cid, tab);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to update post", { variant: "error" });
    }
  };

  const saveEditComment = async () => {
    if (!isEditCommentValid) {
      enqueueSnackbar("Please enter a comment", { variant: "error" });
      return;
    }
    try {
      await backend.put(`/api/community/posts/${editComment.postId}/comment/${editComment.index}`, { userId, text: editCommentText });
      enqueueSnackbar("Comment updated successfully", { variant: "success" });
      setEditComment(null);
      loadPosts(cid, tab);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to update comment", { variant: "error" });
    }
  };

  const deleteCommunity = async () => {
    try {
      await backend.delete(`/api/community/${cid}`, { data: { userId } });
      enqueueSnackbar("Community deleted successfully", { variant: "success" });
      setConfirmDeleteCommunity(false);
      nav(-1);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to delete community", { variant: "error" });
    }
  };

  if (loading || !c) {
    return (
      <Box className="comm-page" sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
        <CircularProgress sx={{ color: BRAND }} />
      </Box>
    );
  }

  return (
    <Box className="comm-page">
      {/* Unified header card */}
      <div className="comm-header-card">
        <img className="comm-cover" src={c.coverImage || "https://picsum.photos/900/450"} alt="cover" loading="lazy" />

        <div className="comm-header-floater comm-toprow">
          <IconButton onClick={() => nav(-1)}><ArrowBack /></IconButton>
          <div />
          {isAdmin ? (
            <IconButton onClick={(e) => setHeaderMenuEl(e.currentTarget)}><MoreVert /></IconButton>
          ) : <div style={{ width: 40 }} />}
        </div>

        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={c.avatar || c.coverImage} sx={{ width: 56, height: 56 }} />
            <Box flex={1} minWidth={0}>
              <Typography className="comm-cname" noWrap>{c.name}</Typography>
              <Typography
                className="comm-cmeta"
                style={{ cursor: isAdmin ? 'pointer' : '' }}
                onClick={() => isAdmin && (setHeaderMenuEl(null), setOpenManage(true))}
              >
                {(c.members?.length ?? 0)} members
              </Typography>
            </Box>
            <Button onClick={toggleJoin} className={`comm-cta ${c._joined ? "joined" : ""}`}>
              {c._joined ? "Leave Group" : "Join"}
            </Button>
          </Stack>

          <Typography className="comm-cdesc" sx={{ mt: 1 }} noWrap>
            {c.description || "Lorem Ipsum is simply dummy text of the printing and typesetting industry."}
          </Typography>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} className="comm-tabs" sx={{ flex: 1 }}>
              <Tab value="all" label="Community post" className="comm-tab" />
              <Tab value="mine" label="Your post" className="comm-tab" />
            </Tabs>
            <Button onClick={() => setOpenCompose(true)} className="comm-cta">Post</Button>
          </Stack>
        </Box>
      </div>

      {/* Posts */}
      <Box sx={{ mt: 1 }}>
        {posts.map(p => {
          const isExp = expanded.has(p._id);
          return (
            <React.Fragment key={p._id}>
              <PostCard
                post={p}
                expanded={isExp}
                onSeeMore={() => { const set = new Set(expanded); set.add(p._id); setExpanded(set); }}
                onLike={() => onLike(p)}
                onComment={() => setOpenCommentsPost(p)}
                onMore={(el) => { setPostMenuEl(el); setPostForMenu(p); }}
              />
              <Divider />
            </React.Fragment>
          );
        })}
      </Box>

      {/* Post menu (anchor) */}
      <Menu anchorEl={postMenuEl} open={Boolean(postMenuEl)} onClose={() => setPostMenuEl(null)}>
        <MenuItem onClick={() => { setEditPost(postForMenu); setPostMenuEl(null); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => { setConfirmDeletePost(postForMenu); setPostMenuEl(null); }} sx={{ color: "error.main" }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Header menu (admin only) */}
      <Menu anchorEl={headerMenuEl} open={Boolean(headerMenuEl)} onClose={() => setHeaderMenuEl(null)}>
        <MenuItem onClick={() => { setHeaderMenuEl(null); setOpenCompose(true); }}>
          <MoreVert fontSize="small" sx={{ mr: 1, opacity: 0 }} /> New post
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => { setHeaderMenuEl(null); setOpenManage(true); }}>
            <MoreVert fontSize="small" sx={{ mr: 1, opacity: 0 }} /> Manage members
          </MenuItem>
        )}
        <MenuItem onClick={() => { setHeaderMenuEl(null); setConfirmDeleteCommunity(true); }} sx={{ color: "error.main" }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete community
        </MenuItem>
      </Menu>

      {/* dialogs */}
      <Confirm
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={actuallyLeave}
        title="Leave community?"
        message="You will stop receiving updates from this group."
        cta="Leave"
        danger
      />
      <Confirm
        open={Boolean(confirmDeletePost)}
        onClose={() => setConfirmDeletePost(null)}
        onConfirm={() => deletePost(confirmDeletePost)}
        title="Delete this post?"
        cta="Delete"
        danger
      />
      <Confirm
        open={Boolean(confirmDeleteComment)}
        onClose={() => setConfirmDeleteComment(null)}
        onConfirm={() => { deleteComment(confirmDeleteComment); setConfirmDeleteComment(null); setOpenCommentsPost(null); }}
        title="Delete this comment?"
        cta="Delete"
        danger
      />
      <Confirm
        open={confirmDeleteCommunity}
        onClose={() => setConfirmDeleteCommunity(false)}
        onConfirm={deleteCommunity}
        title="Delete community?"
        message="This can’t be undone."
        cta="Delete"
        danger
      />

      <ComposeDialog open={openCompose} onClose={() => setOpenCompose(false)} onPublish={onPublish} />

      <CommentsDialog
        post={openCommentsPost}
        onClose={() => setOpenCommentsPost(null)}
        onSend={async (text) => {
          await backend.post(`/api/community/posts/${openCommentsPost._id}/comment`, { userId, text });
          setOpenCommentsPost(null);
          loadPosts(cid, tab);
        }}
        onStartEdit={(index, currentText) => setEditComment({ postId: openCommentsPost._id, index, currentText })}
        onDelete={(index) => setConfirmDeleteComment({ postId: openCommentsPost._id, index })}
      />

      {/* edit post */}
      <Dialog open={Boolean(editPost)} onClose={() => setEditPost(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Edit post
          <IconButton onClick={() => setEditPost(null)} sx={{ float: "right" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            rows={4}
            value={editPostText}
            onChange={e => setEditPostText(e.target.value)}
            fullWidth
            error={!editPostText.trim() && editPostMedia.length === 0}
            helperText={!editPostText.trim() && editPostMedia.length === 0 ? "Description or media is required" : ""}
          />

          {/* Removable media grid */}
          {editPostMedia?.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "var(--subtle)" }}>
                Media ({editPostMedia.length})
              </Typography>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                  gap: 10
                }}
              >
                {editPostMedia.map((m, i) => (
                  <div key={i} style={{ position: "relative", borderRadius: 8, overflow: "hidden" }}>
                    {m.type === "video" ? (
                      <video
                        src={m.url}
                        style={{ width: "100%", height: 110, objectFit: "cover" }}
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img
                        src={m.url}
                        alt=""
                        style={{ width: "100%", height: 110, objectFit: "cover" }}
                        loading="lazy"
                      />
                    )}
                    <IconButton
                      onClick={() => removeEditMediaAt(i)}
                      size="small"
                      aria-label="Remove media"
                      title="Remove"
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        background: "rgba(17,24,39,0.6)",
                        color: "#fff",
                        borderRadius: 8
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mt: 2, color: "var(--subtle)" }}>
              No media attached to this post.
            </Typography

            >
          )}

          {/* Add more media during edit */}
          <Stack direction="row" spacing={1.2} sx={{ mt: 1.5 }}>
            <Button
              onClick={() => handleEditPick("image/*", "image")}
              className="comm-pick"
              fullWidth
              startIcon={<ImageIcon />}
              disabled={editImgBusy}
            >
              {editImgBusy ? <CircularProgress size={18} /> : "Add photo"}
            </Button>
            <Button
              onClick={() => handleEditPick("video/*", "video")}
              className="comm-pick"
              fullWidth
              startIcon={<VideoLibrary />}
              disabled={editVidBusy}
            >
              {editVidBusy ? <CircularProgress size={18} /> : "Add video"}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPost(null)} disabled={editImgBusy || editVidBusy}>Cancel</Button>
          <Button
            onClick={saveEditPost}
            variant="contained"
            disabled={!isEditPostValid || editImgBusy || editVidBusy}
          >
            {(editImgBusy || editVidBusy) ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* edit comment */}
      <Dialog open={Boolean(editComment)} onClose={() => setEditComment(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Edit comment
          <IconButton onClick={() => setEditComment(null)} sx={{ float: "right" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            rows={3}
            value={editCommentText}
            onChange={e => setEditCommentText(e.target.value)}
            fullWidth
            error={!editCommentText.trim()}
            helperText={!editCommentText.trim() ? "Comment is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditComment(null)}>Cancel</Button>
          <Button
            onClick={saveEditComment}
            variant="contained"
            disabled={!isEditCommentValid}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* admin: manage members */}
      <ManageMembersDialog
        communityId={cid}
        open={openManage}
        onClose={() => setOpenManage(false)}
        isAdmin={isAdmin}
        currentUserId={userId}
      />
    </Box>
  );
}

export default CommunityDetails;